import {
  type AskPunttAttachment,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";

import {
  type ChatArtifact,
  type ChatArtifactKind,
  type ChatArtifactSelection,
} from "./artifactTypes";
import { type ConnectorOption } from "./components/InputPlusMenu";

const CANVAS_OUTPUT_TYPE_LABELS: Record<string, string> = {
  risk_assessment: "Risk Assessment",
  brief_document: "Creative Brief",
  comparison: "Comparison",
  report: "Report",
  visualization: "Visualization",
  custom: "Document",
};

// Reduce a URL to its pathname so origin/signature/fragment differences do
// not prevent a match. Falls back to the trimmed input when URL parsing
// fails (e.g. relative paths).
function urlPathname(url: string | undefined | null): string {
  if (!url) return "";
  try {
    return new URL(url).pathname;
  } catch {
    const q = url.indexOf("?");
    const h = url.indexOf("#");
    const end = Math.min(q >= 0 ? q : url.length, h >= 0 ? h : url.length);
    return url.slice(0, end);
  }
}

// Resolve a citation's source URL to an index in the context file list. Signed
// S3 URLs are compared by pathname because the query-string signature can
// differ between when the backend emitted the citation and when the frontend
// fetched the asset. Returns -1 when nothing matches (caller keeps the current
// file selection).
export function findContextFileIndex(
  contextFiles: readonly { source: string }[],
  sourceUrl: string | undefined,
  originalURL: string | undefined,
): number {
  const sourceKey = urlPathname(sourceUrl);
  const originalKey = urlPathname(originalURL);
  if (!sourceKey && !originalKey) return -1;
  for (let i = 0; i < contextFiles.length; i++) {
    const fileKey = urlPathname(contextFiles[i]?.source);
    if (!fileKey) continue;
    if (fileKey === sourceKey || fileKey === originalKey) return i;
  }
  return -1;
}

/**
 * Extract the chronologically-ordered list of unique review ticket IDs from
 * chat messages. Drives the "Review N of M" navigation when a single chat
 * has multiple sequential reviews.
 *
 * Only considers assistant messages with a viewTicketId. The first occurrence
 * of each ticketId wins so order is stable even if a ticket is referenced
 * again later in the conversation.
 */
export function collectReviewTicketIds(
  messages: readonly { role: string; viewTicketId?: string | null }[],
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const msg of messages) {
    if (
      msg.role === "assistant" &&
      msg.viewTicketId &&
      !seen.has(msg.viewTicketId)
    ) {
      seen.add(msg.viewTicketId);
      ids.push(msg.viewTicketId);
    }
  }
  return ids;
}

/**
 * Map ticket ids to display titles from `Create Ticket` search-progress
 * events in the chat transcript. Used by the standalone multi-review rail.
 */
export function collectReviewTicketTitles(
  messages: readonly {
    searchProgress?: { source?: string; results?: unknown[] }[];
  }[],
): Map<string, string> {
  const titleMap = new Map<string, string>();
  for (const msg of messages) {
    for (const sp of msg.searchProgress ?? []) {
      if (sp.source === "Create Ticket") {
        for (const r of (sp.results ?? []) as {
          id?: string;
          title?: string;
        }[]) {
          if (r.id && r.title) titleMap.set(r.id, r.title);
        }
      }
    }
  }
  return titleMap;
}

/**
 * Chronological list of unique files the user uploaded in this chat (deduped
 * by attachment key or source URL). Standalone chats persist uploads on user
 * messages — no separate project-files API.
 */
export function collectChatUploadAttachments(
  messages: readonly {
    role: string;
    attachments?: AskPunttAttachment[] | null;
  }[],
): AskPunttAttachment[] {
  const seen = new Set<string>();
  const out: AskPunttAttachment[] = [];
  for (const msg of messages) {
    if (msg.role !== "user") continue;
    for (const att of msg.attachments ?? []) {
      const dedupeKey = att.key?.trim() || att.source?.trim();
      if (!dedupeKey || seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push(att);
    }
  }
  return out;
}

/**
 * Whether this is the chat's FIRST review — i.e. it has produced at most one
 * review ticket so far.
 *
 * A single-asset review produces exactly one `viewTicketId` (the in-flight
 * first review counts as 1). A *prior* completed review leaves its own ticket,
 * so two or more distinct ticket ids means a review has already finished before
 * the current one.
 *
 * IMPORTANT: we count tickets rather than "any viewTicketId exists" because
 * `viewTicketId` is stamped on the review-triggering assistant message BEFORE
 * `chat.reviewStatus` turns on (the "limbo" window — see `isReviewInLimbo` in
 * chat.tsx). So "any viewTicketId" would already be true during the first
 * review and the suppression would never fire. Counting tickets sidesteps that
 * timing entirely.
 */
export function isFirstReviewForChat(
  reviewTicketIds: readonly string[],
): boolean {
  return reviewTicketIds.length <= 1;
}

/**
 * Whether the right pane should be fully suppressed (chat-only, full-width)
 * because a brand-new single-asset chat is running its first review.
 *
 * Scope (intentional):
 *   - Single-asset, non-project reviews only. Project / multi-asset / killer-
 *     flow chats are EXCLUDED — they keep their canvas + project info visible
 *     during reviews. A chat is "project-flavored" if it has
 *     `workflowType === "project"`, carries `projectData` (`activeProjectData`),
 *     is in the project-loading window, OR is mid killer-flow typewriter
 *     (`isKillerFlowStreaming`). The killer-flow guard matters because the
 *     demo emits `projectData` slightly AFTER the typewriter starts (christo
 *     deploy lag), so without it a demo review would flash chat-only in that
 *     gap.
 *   - Only a "brand-new" chat — one with no canvas artifact yet
 *     (`hasCanvasContent` false). A chat that already produced a canvas (e.g.
 *     an ROI breakdown) and then runs a review is NOT brand-new; blanking that
 *     canvas to go chat-only would hide context the user is actively looking
 *     at, so the right pane is kept and canvas-vs-ticket precedence applies.
 *   - Only the first review (the chat has produced at most one review ticket).
 *   - A stalled review (status held past the stall threshold AND an assistant
 *     reply has already landed) reveals the pane, so a leaked/stuck
 *     `reviewStatus` can never hide findings forever.
 *
 * The `reviewInLimbo` signal closes the gap at the START of a review:
 * rembrandt stamps `viewTicketId` on the assistant message (step 4) BEFORE it
 * sets `chat.reviewStatus` (step 5, ~45s+ later). During that window
 * `isReviewing` is still false but `effectiveViewTicketId` is already set, so
 * without folding limbo in the ticket pane flashes the instant the asset is
 * uploaded — before any findings exist. Treating limbo as "reviewing" keeps
 * the pane suppressed from upload through the entire review; it self-clears the
 * moment the post-review summary lands (no `viewTicketId` on it) or the 15-min
 * limbo cap fires (see `isReviewInLimbo` in chat.tsx).
 */
export function shouldHideRightPaneForFirstReview(args: {
  isProjectChat: boolean;
  hasActiveProjectData: boolean;
  isProjectLoading: boolean;
  isKillerFlowStreaming: boolean;
  hasCanvasContent: boolean;
  isReviewing: boolean;
  reviewInLimbo: boolean;
  reviewStalled: boolean;
  isFirstReview: boolean;
}): boolean {
  const isSingleAssetReview =
    !args.isProjectChat &&
    !args.hasActiveProjectData &&
    !args.isProjectLoading &&
    !args.isKillerFlowStreaming &&
    !args.hasCanvasContent;
  return (
    isSingleAssetReview &&
    (args.isReviewing || args.reviewInLimbo) &&
    args.isFirstReview &&
    !args.reviewStalled
  );
}

/**
 * Extract the chronologically-ordered list of unique assistant message IDs
 * that carry a non-empty canvas (≥1 block). Drives the "Canvas N of M" style
 * navigation when a single chat has multiple sequential canvases — the
 * canvas analogue of `collectReviewTicketIds`.
 *
 * First occurrence of each message id wins so order is stable.
 */
export function collectCanvasMessageIds(
  messages: readonly {
    _id: string;
    role: string;
    canvasContent?: { blocks?: unknown[] } | null;
  }[],
): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const msg of messages) {
    if (
      msg.role === "assistant" &&
      msg.canvasContent &&
      Array.isArray(msg.canvasContent.blocks) &&
      msg.canvasContent.blocks.length > 0 &&
      !seen.has(msg._id)
    ) {
      seen.add(msg._id);
      ids.push(msg._id);
    }
  }
  return ids;
}

type CanvasMessageForTitle = {
  activeSkill?: string | null;
  canvasContent?: {
    outputType?: string;
    blocks?: { type?: string; text?: string; content?: string }[];
  } | null;
};

/**
 * Derive a human-readable sidebar title for a canvas-bearing message.
 * Falls back: activeSkill label -> first heading block -> outputType label.
 */
export function artifactTitleForCanvas(message: CanvasMessageForTitle): string {
  if (message.activeSkill) {
    const skillLabel = ASK_PUNTT_SKILL_LABELS[message.activeSkill];
    if (skillLabel) return skillLabel;
  }
  const blocks = message.canvasContent?.blocks ?? [];
  for (const block of blocks) {
    if (block.type === "heading") {
      const text = (block.text ?? block.content ?? "").trim();
      if (text) return text.length > 60 ? `${text.slice(0, 60)}...` : text;
    }
  }
  const outputType = message.canvasContent?.outputType ?? "custom";
  return CANVAS_OUTPUT_TYPE_LABELS[outputType] ?? "Document";
}

export function canvasSubtitleForMessage(
  message: CanvasMessageForTitle,
): string {
  const outputType = message.canvasContent?.outputType;
  if (!outputType) return "Document";
  return CANVAS_OUTPUT_TYPE_LABELS[outputType] ?? outputType;
}

export type CollectChatArtifactsMessage = {
  _id: string;
  role: string;
  createdAt?: string;
  viewTicketId?: string | null;
  canvasContent?: {
    outputType?: string;
    blocks?: unknown[];
  } | null;
  canvasLoading?: boolean | null;
  activeSkill?: string | null;
  status?: string;
};

export type ReviewSummaryForArtifact = {
  thumbnailUrl?: string | null;
  aiCommentCount?: number;
  humanCommentCount?: number;
  versionNumber?: number;
};

/**
 * Merge review tickets and canvas documents into a single chronological
 * artifact list for the cowork-style sidebar. Reviews and canvases interleave
 * by message order; each ticket id and message id appears at most once.
 */
export function collectChatArtifacts(
  messages: readonly CollectChatArtifactsMessage[],
  titleMap: ReadonlyMap<string, string> = new Map(),
  reviewSummaries: ReadonlyMap<string, ReviewSummaryForArtifact> = new Map(),
): ChatArtifact[] {
  const artifacts: ChatArtifact[] = [];
  const seenReviewTickets = new Set<string>();
  const seenCanvasMessages = new Set<string>();

  for (const msg of messages) {
    if (msg.role !== "assistant") continue;

    if (msg.viewTicketId && !seenReviewTickets.has(msg.viewTicketId)) {
      seenReviewTickets.add(msg.viewTicketId);
      const ticketId = msg.viewTicketId;
      const summary = reviewSummaries.get(ticketId);
      const title =
        titleMap.get(ticketId) ?? `Review ${seenReviewTickets.size}`;
      const isGenerating =
        msg.status === "pending" ||
        (msg.canvasLoading === true && !summary?.aiCommentCount);

      artifacts.push({
        id: ticketId,
        kind: "review",
        messageId: msg._id,
        title,
        createdAt: msg.createdAt,
        ticketId,
        thumbnailUrl: summary?.thumbnailUrl ?? null,
        isGenerating,
        versionNumber: summary?.versionNumber,
        aiCommentCount: summary?.aiCommentCount,
        humanCommentCount: summary?.humanCommentCount,
      });
    }

    const hasCanvasBlocks =
      msg.canvasContent &&
      Array.isArray(msg.canvasContent.blocks) &&
      msg.canvasContent.blocks.length > 0;

    if (hasCanvasBlocks && !seenCanvasMessages.has(msg._id)) {
      seenCanvasMessages.add(msg._id);
      artifacts.push({
        id: msg._id,
        kind: "canvas",
        messageId: msg._id,
        title: artifactTitleForCanvas(msg as CanvasMessageForTitle),
        subtitle: canvasSubtitleForMessage(msg as CanvasMessageForTitle),
        createdAt: msg.createdAt,
        outputType: msg.canvasContent?.outputType,
        isGenerating: msg.canvasLoading === true && msg.status !== "completed",
      });
    } else if (
      msg.canvasLoading === true &&
      msg.status !== "completed" &&
      !seenCanvasMessages.has(msg._id)
    ) {
      seenCanvasMessages.add(msg._id);
      artifacts.push({
        id: msg._id,
        kind: "canvas",
        messageId: msg._id,
        title: artifactTitleForCanvas(msg as CanvasMessageForTitle),
        subtitle: "Generating...",
        createdAt: msg.createdAt,
        outputType: msg.canvasContent?.outputType,
        isGenerating: true,
      });
    }
  }

  return artifacts;
}

export function selectionMatchesArtifact(
  selection: ChatArtifactSelection | null,
  artifact: ChatArtifact,
): boolean {
  if (!selection) return false;
  if (selection.kind === "review" && artifact.kind === "review") {
    return selection.ticketId === artifact.ticketId;
  }
  if (selection.kind === "canvas" && artifact.kind === "canvas") {
    return selection.messageId === artifact.messageId;
  }
  return false;
}

export function artifactKindLabel(kind: ChatArtifactKind): string {
  return kind === "review" ? "Review" : "Document";
}

/**
 * Build the click handler that activates an older review ticket from the
 * chat history. Extracted into a factory so the index lookup + dismissed
 * clear + analytics payload can be unit-tested directly without mounting
 * `chat.tsx`.
 *
 * Stale ticket ids (e.g. a ticket deleted server-side after the chat
 * loaded) silently no-op: no state mutation, no analytics event. The
 * `TicketViewPane` already renders its own "Ticket not found" state, but
 * we want a stale click to be a non-event from the chat's perspective.
 */
export interface CreateActivateTicketHandlerDeps {
  allReviewTicketIds: readonly string[];
  setSelectedReviewIndex: (idx: number) => void;
  setTicketDismissed: (dismissed: boolean) => void;
  capture: (event: string, props: Record<string, unknown>) => void;
  chatId: string | undefined;
}

export function createActivateTicketHandler(
  deps: CreateActivateTicketHandlerDeps,
): (ticketId: string) => void {
  return (ticketId: string) => {
    const idx = deps.allReviewTicketIds.indexOf(ticketId);
    if (idx < 0) return;
    deps.setSelectedReviewIndex(idx);
    deps.setTicketDismissed(false);
    deps.capture("ask_puntt_previous_artifact_clicked", {
      artifact: "ticket",
      ticket_id: ticketId,
      chat_id: deps.chatId,
    });
  };
}

/**
 * D8 streaming-suppression predicate. Walks the latest assistant message's
 * `agentSteps`, pairing tool_calls with their tool_results positionally
 * per tool name. If at least one unresolved (open) tool_call has a tool
 * name in `artifactProducingTools`, returns true — older clickable artifact
 * rows should grey out so a stale click can't yank the right pane while
 * a new artifact is mid-stream.
 *
 * The check is intentionally narrow: it fires only when the LAST message
 * is an in-flight assistant turn. Completed turns don't block. A turn
 * that calls `puntt_search` (non-artifact) doesn't block.
 */
export function computeIsAnotherArtifactStreaming(
  messages: readonly {
    role: string;
    status?: string;
    agentSteps?: { type: string; tool?: string }[];
  }[],
  artifactProducingTools: ReadonlySet<string>,
): boolean {
  const lastMsg = messages[messages.length - 1];
  if (!lastMsg || lastMsg.role !== "assistant") return false;
  if (lastMsg.status === "completed") return false;
  if (!lastMsg.agentSteps) return false;
  const open = new Map<string, number>();
  for (const step of lastMsg.agentSteps) {
    if (step.type === "tool_call" && step.tool) {
      open.set(step.tool, (open.get(step.tool) ?? 0) + 1);
    } else if (step.type === "tool_result" && step.tool) {
      open.set(step.tool, (open.get(step.tool) ?? 0) - 1);
    }
  }
  let found = false;
  open.forEach((count, tool) => {
    if (count > 0 && artifactProducingTools.has(tool)) found = true;
  });
  return found;
}

/**
 * Canvas counterpart to `createActivateTicketHandler`. Stale message ids
 * (canvas wiped by a backend re-render, message dropped from `localMessages`,
 * etc.) no-op the same way so the click is never a destructive operation.
 */
export interface CreateActivateCanvasHandlerDeps {
  canvasMessageIds: readonly string[];
  setSelectedCanvasMessageId: (id: string | null) => void;
  setCanvasDismissed: (dismissed: boolean) => void;
  capture: (event: string, props: Record<string, unknown>) => void;
  chatId: string | undefined;
}

export function createActivateCanvasHandler(
  deps: CreateActivateCanvasHandlerDeps,
): (messageId: string) => void {
  return (messageId: string) => {
    if (!deps.canvasMessageIds.includes(messageId)) return;
    deps.setSelectedCanvasMessageId(messageId);
    deps.setCanvasDismissed(false);
    deps.capture("ask_puntt_previous_artifact_clicked", {
      artifact: "canvas",
      message_id: messageId,
      chat_id: deps.chatId,
    });
  };
}

/** Convert chat's enabledConnectors array to Record for display */
export function connectorsArrayToRecord(
  ids: string[] | undefined,
  available: ConnectorOption[],
): Record<string, boolean> {
  const record: Record<string, boolean> = {};
  available.forEach((c) => {
    record[c.id] = ids?.includes(c.id) ?? false;
  });
  return record;
}

/**
 * Monotonic merge: never regress append-only arrays or clear fields
 * that haven't been replaced by final content yet. Prevents stale poll
 * responses from overwriting locally accumulated status data.
 */
export type AskPunttMessageExt = AskPunttMessage & {
  canvasChangedSectionIds?: string[];
  projectLoading?: boolean | null;
  // Set by the shared-chat agent when it mutated the project this turn. The
  // campaign workspace refetches the project on it (docs panel + header).
  projectMutated?: boolean | null;
};

/**
 * Display-only messages are read by the agent but excluded from the rendered
 * timeline (e.g. the synthesized transcript a shared private chat is ingested
 * as — the user only wants the "Shared the chat…" feed note plus the agent's
 * outcome turn, not the verbose transcript). Strict `=== true` so an absent or
 * `false` flag always renders.
 */
export const isHiddenMessage = (
  m: Pick<AskPunttMessageExt, "hidden">,
): boolean => m.hidden === true;

/**
 * Prefer the richer projectData object. If incoming is missing or emptier than
 * the local copy, keep local to avoid regressing progressively streamed fields.
 */
export function mergeProjectData(
  local: AskPunttMessage["projectData"],
  incoming: AskPunttMessage["projectData"],
): AskPunttMessage["projectData"] {
  if (!incoming) return local;
  if (!local) return incoming;
  const localKeys = Object.keys(local).filter(
    (k) => (local as Record<string, unknown>)[k] != null,
  );
  const incomingKeys = Object.keys(incoming).filter(
    (k) => (incoming as Record<string, unknown>)[k] != null,
  );
  return incomingKeys.length >= localKeys.length ? incoming : local;
}

export function mergeMessageStatus(
  local: AskPunttMessageExt,
  incoming: AskPunttMessageExt,
): AskPunttMessageExt {
  return {
    ...local,
    agentSteps:
      (incoming.agentSteps?.length ?? 0) >= (local.agentSteps?.length ?? 0)
        ? incoming.agentSteps
        : local.agentSteps,
    searchProgress:
      (incoming.searchProgress?.length ?? 0) >=
      (local.searchProgress?.length ?? 0)
        ? incoming.searchProgress
        : local.searchProgress,
    streamingContent:
      incoming.streamingContent !== null &&
      incoming.streamingContent !== undefined
        ? incoming.streamingContent
        : incoming.content
          ? incoming.streamingContent
          : local.streamingContent,
    content: incoming.content || local.content,
    status:
      local.status === "completed"
        ? "completed"
        : (incoming.status ?? local.status),
    structuredContent: incoming.structuredContent ?? local.structuredContent,
    canvasContent: incoming.canvasContent ?? local.canvasContent,
    canvasLoading: incoming.canvasContent
      ? incoming.canvasLoading
      : (incoming.canvasLoading ?? local.canvasLoading),
    canvasChangedSectionIds:
      incoming.canvasChangedSectionIds ?? local.canvasChangedSectionIds,
    viewTicketId: incoming.viewTicketId ?? local.viewTicketId,
    suggestedResponses: incoming.suggestedResponses ?? local.suggestedResponses,
    userQuestions: incoming.userQuestions ?? local.userQuestions,
    projectData: mergeProjectData(local.projectData, incoming.projectData),
    projectLoading: incoming.projectData
      ? incoming.projectLoading
      : (incoming.projectLoading ?? local.projectLoading),
    contentBlocks: incoming.contentBlocks ?? local.contentBlocks,
    feedback: incoming.feedback ?? local.feedback,
    projectMutated: incoming.projectMutated ?? local.projectMutated,
    mentions:
      incoming.role === "user"
        ? (incoming.mentions ?? local.mentions)
        : local.mentions,
  };
}

export type FeedActorMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

/** True when the signed-in user authored this feed user message. */
export function isFeedMessageFromViewer(
  message: { userId?: string },
  options: { currentUserId?: string; chatOwnerId?: string },
): boolean {
  if (!options.currentUserId) return false;
  const authorId =
    message.userId ?? options.chatOwnerId ?? options.currentUserId;
  return authorId === options.currentUserId;
}

/** Resolve avatar + display name for a user message in project feed presentation. */
export function resolveFeedActorForUserMessage(
  message: { userId?: string },
  options: {
    currentUserId?: string;
    chatOwnerId?: string;
    members?: FeedActorMember[];
    viewerName?: string;
    viewerAvatar?: string | null;
  },
): { name: string; avatar?: string | null } {
  const authorId =
    message.userId ?? options.chatOwnerId ?? options.currentUserId;
  const member = authorId
    ? options.members?.find((m) => m._id === authorId)
    : undefined;

  if (member) {
    return {
      name: member.name ?? member.email ?? "Member",
      avatar: member.avatar ?? undefined,
    };
  }

  // Member list may be stale; use viewer profile only for the signed-in author.
  if (authorId && options.currentUserId && authorId === options.currentUserId) {
    return {
      name: options.viewerName ?? "Member",
      avatar: options.viewerAvatar ?? undefined,
    };
  }

  return { name: "Member", avatar: undefined };
}

/**
 * Sentinel actor id for the AI agent in feed @mentions. Stored on the message
 * `mentions[]` alongside member `_id`s and surfaced as the `@puntt` chip.
 */
export const PUNTT_MENTION_ID = "puntt";

/** An actor (member or @puntt) that can be mentioned in the project feed. */
export type MentionActor = { id: string; name: string };

/** Shared chip styling for @mentions in the feed composer and message bubbles. */
export const MENTION_HIGHLIGHT_CLASS =
  "rounded bg-puntt-accent-3 px-0.5 font-medium text-puntt-accent-11";

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Regex matching an actor's `@token` in message text. `@puntt` matches
 * case-insensitively on a word boundary; members match their exact
 * `@<name>` token (names may contain spaces, so no trailing boundary).
 */
function mentionTokenRegex(actor: MentionActor, global: boolean): RegExp {
  if (actor.id === PUNTT_MENTION_ID) {
    return new RegExp("@puntt\\b", global ? "gi" : "i");
  }
  return new RegExp(`@${escapeRegExp(actor.name)}`, global ? "g" : "");
}

/**
 * Given the composer text and the actors the user picked from the dropdown,
 * return the ids whose `@token` is still present in the text. Deleting a
 * token from the textarea therefore drops its mention from the payload.
 */
export function resolveActiveMentions(
  text: string,
  selected: MentionActor[],
): string[] {
  return getActiveMentionActors(text, selected)
    .filter((a) => a.id !== PUNTT_MENTION_ID && !a.id.startsWith("email:"))
    .map((a) => a.id);
}

/** Split active composer mentions into user ids (incl. puntt) and invite emails. */
export function resolveMentionPayload(
  text: string,
  selected: MentionActor[],
): { mentions: string[]; mentionEmails: string[] } {
  const active = getActiveMentionActors(text, selected);
  const mentions: string[] = [];
  const mentionEmails: string[] = [];
  for (const actor of active) {
    if (actor.id.startsWith("email:")) {
      mentionEmails.push(actor.name);
    } else {
      mentions.push(actor.id);
    }
  }
  return { mentions, mentionEmails };
}

export function emailMentionId(email: string): string {
  return `email:${email.trim().toLowerCase()}`;
}

/** Actors whose `@token` is still present in the composer or message text. */
export function getActiveMentionActors(
  text: string,
  selected: MentionActor[],
): MentionActor[] {
  const actors: MentionActor[] = [];
  for (const actor of selected) {
    if (actors.some((a) => a.id === actor.id)) continue;
    if (mentionTokenRegex(actor, false).test(text)) actors.push(actor);
  }
  return actors;
}

/**
 * Resolve mention actors for a feed user message so `@tokens` can render as
 * highlighted chips. Uses persisted `mentions` ids + project members first;
 * when ids are missing (pre-deploy API) falls back to matching `@Name` tokens
 * in the message body against `members`.
 */
export function resolveMentionActorsForFeedMessage(
  content: string,
  mentionIds: string[] | undefined,
  members: FeedActorMember[] | undefined,
  mentionEmails?: string[] | undefined,
): MentionActor[] {
  const actors: MentionActor[] = [];
  const ids = mentionIds ?? [];

  for (const id of ids) {
    if (id === PUNTT_MENTION_ID) {
      const actor = { id, name: "Puntt" };
      if (mentionTokenRegex(actor, false).test(content)) actors.push(actor);
      continue;
    }
    const member = members?.find((m) => m._id === id);
    const name = member?.name ?? member?.email;
    if (!name) continue;
    const actor = { id, name };
    if (mentionTokenRegex(actor, false).test(content)) actors.push(actor);
  }

  for (const email of mentionEmails ?? []) {
    const actor = { id: emailMentionId(email), name: email };
    if (
      !actors.some((a) => a.id === actor.id) &&
      mentionTokenRegex(actor, false).test(content)
    ) {
      actors.push(actor);
    }
  }

  if (actors.length > 0) return actors;

  for (const member of members ?? []) {
    const name = member.name ?? member.email;
    if (!name) continue;
    const actor = { id: member._id, name };
    if (
      !actors.some((a) => a.id === actor.id) &&
      mentionTokenRegex(actor, false).test(content)
    ) {
      actors.push(actor);
    }
  }
  const puntt = { id: PUNTT_MENTION_ID, name: "Puntt" };
  if (
    !actors.some((a) => a.id === puntt.id) &&
    mentionTokenRegex(puntt, false).test(content)
  ) {
    actors.push(puntt);
  }
  return actors;
}

/** Keep mention metadata when the server round-trip omits it (stale deploy). */
export function preserveUserMessageMentions<T extends AskPunttMessage>(
  server: T,
  local?: AskPunttMessage | null,
): T {
  if (server.role !== "user") return server;
  const hasServerMentions =
    !!server.mentions?.length || !!server.mentionEmails?.length;
  if (hasServerMentions) return server;
  const hasLocalMentions =
    !!local?.mentions?.length || !!local?.mentionEmails?.length;
  if (!hasLocalMentions) return server;
  return {
    ...server,
    mentions: local?.mentions?.length ? local.mentions : server.mentions,
    mentionEmails: local?.mentionEmails?.length
      ? local.mentionEmails
      : server.mentionEmails,
  };
}

/** Reconcile polled chat history with local user messages (mentions, etc.). */
export function mergeServerMessagesWithLocal(
  serverMessages: AskPunttMessageExt[],
  localMessages: AskPunttMessageExt[],
): AskPunttMessageExt[] {
  return serverMessages.map((serverMsg) => {
    const local = localMessages.find((m) => m._id === serverMsg._id);
    return preserveUserMessageMentions(serverMsg, local) as AskPunttMessageExt;
  });
}

export type MentionSegment =
  | { type: "text"; text: string }
  | { type: "mention"; id: string; label: string };

/**
 * Split message content into plain-text and mention segments so the feed can
 * highlight `@Name` / `@puntt` tokens as chips. `actors` are the resolved
 * mentions (id + display name) for this message; pass an empty array to render
 * the content as a single text segment.
 */
export function renderMentionSegments(
  content: string,
  actors: MentionActor[],
): MentionSegment[] {
  if (!content) return [];
  if (!actors.length) return [{ type: "text", text: content }];

  const matches: { start: number; end: number; id: string; label: string }[] =
    [];
  for (const actor of actors) {
    const re = mentionTokenRegex(actor, true);
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      matches.push({
        start: m.index,
        end: m.index + m[0].length,
        id: actor.id,
        label: m[0],
      });
      // Guard against zero-length matches looping forever.
      if (m.index === re.lastIndex) re.lastIndex++;
    }
  }
  // Earliest first; on a tie the longer match wins so "@Ann Marie" beats "@Ann".
  matches.sort((a, b) => a.start - b.start || b.end - a.end);

  const segments: MentionSegment[] = [];
  let cursor = 0;
  for (const mt of matches) {
    if (mt.start < cursor) continue; // overlaps a chip already emitted
    if (mt.start > cursor) {
      segments.push({ type: "text", text: content.slice(cursor, mt.start) });
    }
    segments.push({ type: "mention", id: mt.id, label: mt.label });
    cursor = mt.end;
  }
  if (cursor < content.length) {
    segments.push({ type: "text", text: content.slice(cursor) });
  }
  return segments;
}

/**
 * Reject a persisted chat-heavy two-pane split for ticket/canvas views (from
 * a legacy 50/50-era persistence or a manual drag) and fall back to the
 * review-biased default. Guards `raw.length < 2` first — a short/malformed
 * persisted array (corrupted localStorage, an old schema) must not read past
 * its end before the ratio check runs.
 */
export function clampTwoPaneLayout(
  raw: readonly number[],
  defaultLayout: readonly number[],
): number[] {
  // The `raw.length < 2` check is load-bearing on its own: without it, a
  // short/corrupt array reads `raw[1]` as `undefined`, and `undefined < 50`
  // evaluates to `false` (not `true`) — so a malformed single-element layout
  // would silently pass the ratio check below and render unclamped instead
  // of falling back to `defaultLayout`.
  if (raw.length < 2 || raw[0] > 45 || raw[1] < 50) {
    return [...defaultLayout];
  }
  return [...raw];
}

/** Human-readable labels for Ask Puntt ``activeSkill`` values in the chat UI. */
export const ASK_PUNTT_SKILL_LABELS: Record<string, string> = {
  claim_generation: "Claims Verification",
  compliance_review: "Compliance Review",
  artwork_review: "Artwork Review",
  brief_creation: "Brief Writing",
  compare: "Brief Comparison",
  project: "Project",
  puntt_actions: "Puntt Capabilities",
  puntt_value: "Workflow Analysis",
  meeting_roi: "ROI Workshop",
};
